/**
 * The only place the app talks to the server.
 *  - the access token lives in memory (never localStorage); the refresh token is an httpOnly cookie the page cannot read;
 *  - a 401 triggers one silent refresh and one retry; if that fails the session is over;
 *  - money-moving requests carry an Idempotency-Key supplied by the caller, so a retry can never double-post.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fields: { path: string; message: string }[] = [],
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

const BASE = '/api';
let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;
let refreshing: Promise<boolean> | null = null;

export const setAccessToken = (t: string | null): void => {
  accessToken = t;
};
/** For the live channel, which reads its own stream and so needs the sign-in itself. */
export const currentToken = (): string | null => accessToken;
export const setSessionLostHandler = (fn: (() => void) | null): void => {
  onSessionLost = fn;
};

async function parseError(res: Response): Promise<ApiError> {
  let body: { error?: { code?: string; message?: string; fields?: { path: string; message: string }[]; requestId?: string } } = {};
  try {
    body = await res.json();
  } catch {
    /* not JSON */
  }
  const e = body.error;
  return new ApiError(res.status, e?.code ?? 'UNKNOWN', e?.message ?? 'Something went wrong. Please try again.', e?.fields ?? [], e?.requestId);
}

/**
 * One refresh at a time, however many requests failed together. The server rotates the refresh token on every
 * use and treats a replayed one as theft, so two refreshes must never run with the same cookie.
 */
let lastRefreshTemporary = false;

export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, { method: 'POST', headers: { 'X-Requested-With': 'aa-hotel' }, credentials: 'same-origin' });
      if (!res.ok) {
        // 401 means the session really is over; a busy server (429, 5xx) or a dropped connection does not
        lastRefreshTemporary = res.status === 429 || res.status >= 500;
        return false;
      }
      lastRefreshTemporary = false;
      const data = (await res.json()) as { accessToken: string };
      accessToken = data.accessToken;
      return true;
    } catch {
      lastRefreshTemporary = true;
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** A change that can wait for the connection: what to send, and what to call it on screen. */
export interface QueuedCall {
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  body?: unknown;
  idempotencyKey: string;
  label: string;
}

/**
 * Opt in to "save it for later if the connection is down". Only for actions that are safe to repeat and safe to send
 * late. Money-moving requests never use this. `optimistic` makes the screen show the change straight away.
 */
export interface QueueOption {
  label: string;
  optimistic?: () => void;
}

/** Returns true only if the change was really saved for later (nobody signed in means nowhere to keep it). */
let queuer: ((call: QueuedCall) => boolean) | null = null;
export const setQueuer = (fn: ((call: QueuedCall) => boolean) | null): void => {
  queuer = fn;
};

/** What a queued request resolves to, so the caller knows it was saved rather than done. */
export interface Queued {
  queued: true;
}
export const isQueued = (v: unknown): v is Queued => typeof v === 'object' && v !== null && (v as { queued?: unknown }).queued === true;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** return the response as a file instead of JSON (for CSV downloads) */
  raw?: boolean;
  queue?: QueueOption;
}

export async function api<T>(path: string, opts: RequestOptions = {}, retried = false): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const tokenSent = accessToken;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      credentials: 'same-origin',
      ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch {
    const method = opts.method ?? 'GET';
    if (opts.queue && queuer && method !== 'GET') {
      const saved = queuer({ method, path, ...(opts.body !== undefined ? { body: opts.body } : {}), idempotencyKey: opts.idempotencyKey ?? crypto.randomUUID(), label: opts.queue.label });
      if (saved) {
        opts.queue.optimistic?.();
        return { queued: true } as T;
      }
    }
    throw new ApiError(0, 'NETWORK', "Can't reach the hotel server. Check the connection and try again.");
  }

  if (res.status === 401 && !retried && !path.startsWith('/auth/')) {
    // Another request may already have refreshed while this one was in flight: just retry with the newer token.
    if (accessToken !== null && accessToken !== tokenSent) return api<T>(path, opts, true);
    if (await refreshSession()) return api<T>(path, opts, true);
    if (!lastRefreshTemporary) onSessionLost?.();
    throw await parseError(res);
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  if (opts.raw) return (await res.blob()) as T;
  return (await res.json()) as T;
}

/** Fetches a file with the signed-in session and hands it to the browser as a download. */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const blob = await api<Blob>(path, { raw: true });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export const newKey = (): string => crypto.randomUUID();
